import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { GenericFeaturePermissionService } from '../generic-feature-permission-service';
import { CheckFeaturePermission } from '../../../decorators/feature-permission.decorator';
import type { FeatureKey } from '../../../domain/permission';

/**
 * 通用功能权限检查服务实现
 * @description 提供通用的权限检查能力，可以动态检查任何功能ID的权限
 */
@injectable()
export class GenericFeaturePermissionServiceImpl implements GenericFeaturePermissionService {
    /**
     * 功能ID到方法的映射
     * @private
     */
    private _featureMethodMap = new Map<string, Function>();

    /**
     * 检查功能权限并执行回调
     * @description 动态创建带装饰器的方法并执行
     */
    checkAndExecute<T = any>(featureId: FeatureKey, callback: () => T): T {
        // 获取或创建该功能的执行方法
        const method = this._getOrCreateMethod(featureId, false);
        return method.call(this, callback) as T;
    }

    /**
     * 检查功能权限并执行异步回调
     */
    async checkAndExecuteAsync<T = any>(featureId: FeatureKey, callback: () => Promise<T>): Promise<T> {
        // 获取或创建该功能的执行方法
        const method = this._getOrCreateMethod(featureId, true);
        return method.call(this, callback) as Promise<T>;
    }

    /**
     * 获取或创建功能的执行方法
     * @private
     */
    private _getOrCreateMethod(featureId: FeatureKey, isAsync: boolean): Function {
        const cacheKey = `${featureId}_${isAsync ? 'async' : 'sync'}`;

        if (this._featureMethodMap.has(cacheKey)) {
            return this._featureMethodMap.get(cacheKey)!;
        }

        // 动态创建带权限检查装饰器的方法
        const methodName = `_execute_${featureId.replace(/\./g, '_')}`;

        if (isAsync) {
            // 创建异步方法
            (this as any)[methodName] = async function (callback: () => Promise<any>) {
                console.log(`[Generic Permission Service] 检查权限: ${featureId}`);
                return await callback();
            };
        } else {
            // 创建同步方法
            (this as any)[methodName] = function (callback: () => any) {
                console.log(`[Generic Permission Service] 检查权限: ${featureId}`);
                return callback();
            };
        }

        // 应用装饰器
        const descriptor = Object.getOwnPropertyDescriptor(this, methodName)!;
        const decoratedDescriptor = CheckFeaturePermission(featureId)(
            this,
            methodName,
            descriptor,
        );

        if (decoratedDescriptor) {
            Object.defineProperty(this, methodName, decoratedDescriptor);
        }

        const method = (this as any)[methodName].bind(this);
        this._featureMethodMap.set(cacheKey, method);

        return method;
    }
}
